<div class="mb-6 flex flex-wrap items-center justify-between gap-4">
  <div>
    <h1 class="!mt-0 !mb-2">Team</h1>
    <p class="!mt-0 text-muted-color">
      Manage members, invitations, and access permissions.
    </p>
  </div>
  <div class="flex items-center gap-3">
    @if (canManageTeam) {
      <!-- A disabled button swallows hover events, so the tooltip sits on a wrapper. -->
      <span
        [pTooltip]="
          inviteDisabled
            ? 'All seats are in use. Remove a member or revoke a pending invitation to invite someone else.'
            : ''
        "
        tooltipPosition="bottom"
      >
        <p-button
          label="Invite User"
          icon="pi pi-plus"
          [disabled]="inviteDisabled"
          (onClick)="inviteDialogVisible.set(true)"
        />
      </span>
    }
  </div>
</div>

<p-confirmdialog />

<app-invite-member-dialog
  [(visible)]="inviteDialogVisible"
  [roleOptions]="inviteRoleOptions"
  (invite)="sendInvite($event)"
/>

<div class="mb-4 flex items-center justify-between gap-4">
  <p-tabs
    [value]="activeTab()"
    (valueChange)="activeTab.set($any($event))"
    class="min-w-0 flex-1"
  >
    <p-tablist>
      <p-tab value="current">Current ({{ sortedUsersInAccount.length }})</p-tab>
      <p-tab value="pending">Pending ({{ invitations.length }})</p-tab>
    </p-tablist>
  </p-tabs>
  <span
    class="shrink-0 rounded-full bg-surface-switch-200 px-3 py-1.5 text-sm text-surface-switch-800"
  >
    {{ currentSeats }}/{{ maxSeats }} Seats
  </span>
</div>

@if (activeTab() === "current") {
  <div class="rounded-xl border border-surface-switch-200 shadow-sm">
    <div
      class="hidden lg:grid lg:grid-cols-12 rounded-t-xl bg-surface-switch-100 items-center px-4 py-3 text-sm font-bold text-surface-switch-600 uppercase"
    >
      <div class="col-span-4">Email</div>
      <div class="col-span-2">Date Joined</div>
      <div class="col-span-3">Permissions</div>
      <div class="col-span-3 text-right">Action</div>
    </div>
    @if (sortedUsersInAccount.length === 0) {
      <div class="p-8 text-center text-muted-color">
        <i class="pi pi-users text-4xl"></i>
        <p class="mt-2 mb-0">No current members.</p>
      </div>
    } @else {
      @for (userInAccount of sortedUsersInAccount; track userInAccount.id) {
        <div
          class="border-b border-surface-switch-200 last:border-b-0 bg-surface-switch-50 transition-colors duration-200 hover:bg-surface-switch-100"
        >
          <div class="flex flex-col gap-3 p-4 lg:hidden">
            <div class="font-medium">
              {{ userInAccount.email }}
              @if (userInAccount.id === currentUser?.id) {
                <span class="ml-2 text-muted-color">(you)</span>
              }
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-muted-color">Permissions</span>
              <span class="capitalize">{{ userInAccount.role }}</span>
            </div>
            <div class="flex justify-end">
              <span
                [pTooltip]="
                  userInAccount.id === currentUser?.id ? '' : adminOnlyTooltip
                "
                tooltipPosition="left"
              >
                <p-button
                  size="small"
                  severity="danger"
                  [label]="
                    userInAccount.id === currentUser?.id
                      ? 'Leave Account'
                      : 'Remove'
                  "
                  [disabled]="
                    !canManageTeam && userInAccount.id !== currentUser?.id
                  "
                  (onClick)="
                    userInAccount.id === currentUser?.id
                      ? confirmLeaveAccount()
                      : confirmRemoveUser(userInAccount)
                  "
                />
              </span>
            </div>
          </div>
          <div class="hidden lg:grid lg:grid-cols-12 items-center px-4 py-3">
            <div class="col-span-4 text-sm font-medium">
              {{ userInAccount.email }}
              @if (userInAccount.id === currentUser?.id) {
                <span class="ml-2 text-muted-color">(you)</span>
              }
            </div>
            <div class="col-span-2 text-sm text-muted-color">
              {{ userInAccount.created_at | date: "longDate" }}
            </div>
            <div class="col-span-3">
              @if (canManageTeam && userInAccount.id !== currentUser?.id) {
                <p-select
                  [ngModel]="userInAccount.role"
                  [options]="roleOptions"
                  optionLabel="label"
                  optionValue="value"
                  (onChange)="changeUserRole(userInAccount, $event.value)"
                  appendTo="body"
                  class="w-full"
                />
              } @else {
                <span class="capitalize text-sm">{{ userInAccount.role }}</span>
              }
            </div>
            <div class="col-span-3 text-right">
              <span
                [pTooltip]="
                  userInAccount.id === currentUser?.id ? '' : adminOnlyTooltip
                "
                tooltipPosition="left"
              >
                <p-button
                  size="small"
                  severity="danger"
                  [label]="
                    userInAccount.id === currentUser?.id
                      ? 'Leave Account'
                      : 'Remove'
                  "
                  [disabled]="
                    !canManageTeam && userInAccount.id !== currentUser?.id
                  "
                  (onClick)="
                    userInAccount.id === currentUser?.id
                      ? confirmLeaveAccount()
                      : confirmRemoveUser(userInAccount)
                  "
                />
              </span>
            </div>
          </div>
        </div>
      }
    }
  </div>
} @else {
  <div class="rounded-xl border border-surface-switch-200 shadow-sm">
    <div
      class="hidden lg:grid lg:grid-cols-12 rounded-t-xl bg-surface-switch-100 items-center px-4 py-3 text-sm font-bold text-surface-switch-600 uppercase"
    >
      <div class="col-span-4">Email</div>
      <div class="col-span-2">Permissions</div>
      <div class="col-span-2">Date Invited</div>
      <div class="col-span-2">Expiry Date</div>
      <div class="col-span-2 text-right">Action</div>
    </div>
    @if (invitations.length === 0) {
      <div class="p-8 text-center text-muted-color">
        <i class="pi pi-envelope text-4xl"></i>
        <p class="mt-2 mb-0">No pending invitations.</p>
      </div>
    } @else {
      @for (invitation of invitations; track invitation.id) {
        <div
          class="border-b border-surface-switch-200 last:border-b-0 bg-surface-switch-50 transition-colors duration-200 hover:bg-surface-switch-100"
        >
          <div class="flex flex-col gap-3 p-4 lg:hidden">
            <div class="font-medium">{{ invitation.email }}</div>
            <div class="flex justify-between text-sm">
              <span class="text-muted-color">Permissions</span>
              <span class="capitalize">{{ invitation.role }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-muted-color">Date Invited</span>
              <span>{{ invitation.created_at | date: "mediumDate" }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-muted-color">Expiry Date</span>
              <span class="flex items-center gap-2">
                {{ (invitation.expires_at | date: "mediumDate") || "—" }}
                @if (invitation.is_expired) {
                  <i
                    class="pi pi-exclamation-triangle text-orange-500"
                    role="img"
                    aria-label="Expired"
                    pTooltip="This invitation has expired. Resend it to give them a new link."
                    tooltipPosition="left"
                  ></i>
                }
              </span>
            </div>
            <div class="flex justify-end gap-2">
              <span [pTooltip]="adminOnlyTooltip" tooltipPosition="left">
                <p-button
                  size="small"
                  severity="secondary"
                  label="Resend"
                  icon="pi pi-send"
                  [disabled]="!canManageTeam"
                  (onClick)="resendInvitation(invitation)"
                />
              </span>
              <span [pTooltip]="adminOnlyTooltip" tooltipPosition="left">
                <p-button
                  size="small"
                  severity="danger"
                  label="Revoke"
                  icon="pi pi-trash"
                  [disabled]="!canManageTeam"
                  (onClick)="confirmRevokeInvitation(invitation)"
                />
              </span>
            </div>
          </div>
          <div class="hidden lg:grid lg:grid-cols-12 items-center px-4 py-3">
            <div class="col-span-4 text-sm font-medium">
              {{ invitation.email }}
            </div>
            <div class="col-span-2 capitalize text-sm">
              {{ invitation.role }}
            </div>
            <div class="col-span-2 text-sm text-muted-color">
              {{ invitation.created_at | date: "mediumDate" }}
            </div>
            <div
              class="col-span-2 flex items-center gap-2 text-sm text-muted-color"
            >
              {{ (invitation.expires_at | date: "mediumDate") || "—" }}
              @if (invitation.is_expired) {
                <i
                  class="pi pi-exclamation-triangle text-orange-500"
                  role="img"
                  aria-label="Invitation Expired"
                  pTooltip="This invitation has expired. Resend it to give them a new link."
                  tooltipPosition="top"
                ></i>
              }
            </div>
            <div class="col-span-2 flex justify-end gap-2">
              <span [pTooltip]="adminOnlyTooltip" tooltipPosition="top">
                <p-button
                  size="small"
                  severity="secondary"
                  label="Resend"
                  icon="pi pi-send"
                  [disabled]="!canManageTeam"
                  (onClick)="resendInvitation(invitation)"
                />
              </span>
              <span [pTooltip]="adminOnlyTooltip" tooltipPosition="top">
                <p-button
                  size="small"
                  severity="danger"
                  label="Revoke"
                  icon="pi pi-trash"
                  [disabled]="!canManageTeam"
                  (onClick)="confirmRevokeInvitation(invitation)"
                />
              </span>
            </div>
          </div>
        </div>
      }
    }
  </div>
}
